import { toast } from 'sonner';
import { useNavigate } from 'react-router';
import { useState, useEffect, useCallback } from 'react';

import { useTranslate } from 'src/locales';
import { useChannelContext } from 'src/pages/channel/hooks';
import {
  MICROSOFT_SCOPES,
  MICROSOFT_CLIENT_ID,
  MICROSOFT_TENANT_ID,
  MICROSOFT_REDIRECT_URl,
} from 'src/config';

export const useMicrosoftTeamsAuthPopup = () => {
  const MSAuthConfig = {
    clientId: MICROSOFT_CLIENT_ID,
    tenantId: MICROSOFT_TENANT_ID,
    redirectUri: MICROSOFT_REDIRECT_URl,
    scopes: MICROSOFT_SCOPES,
  };

  const navigate = useNavigate();
  const { t } = useTranslate('translation');
  const { state: channelState, handleMicrosoftDetail } = useChannelContext();
  const [isTeamsConnected, setIsTeamsConnected] = useState(false);

  const { emailProviderType, microsoftEmailDetails } = channelState;

  const state = encodeURIComponent(JSON.stringify({ step: 2, provider: 'MICROSOFTTEAMS' }));

  const loginWithMicrosoftTeams = async () => {
    const authUrl = `https://login.microsoftonline.com/${MSAuthConfig.tenantId}/oauth2/v2.0/authorize?client_id=${MSAuthConfig.clientId}&response_type=code&redirect_uri=${encodeURIComponent(
      MSAuthConfig.redirectUri
    )}&response_mode=query&scope=${encodeURIComponent(MSAuthConfig.scopes)}&prompt=select_account&state=${state}`;

    window.location.href = authUrl;
  };

  const handleMicrosoftTeamsRedirect = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const stateParam = urlParams.get('state');

    if (code) {
      let parsedState: { step?: number; provider?: string; id?: string } = {};
      try {
        parsedState = JSON.parse(decodeURIComponent(stateParam || '{}'));
      } catch (err) {
        console.warn('Invalid state param:', err);
      }

      if (parsedState.provider === 'MICROSOFTTEAMS') {
        handleMicrosoftDetail(code);
        setIsTeamsConnected(true);
        toast.success(`${t('MICROSOFT_TEAMS')} ${t('ACCOUNT_VERIFIED_SUCCESSFULLY')}`);

        if (parsedState.step) {
          navigate(`/channel/step?step=${parsedState.step}`, { replace: true });
        } else {
          navigate('/channel/step', { replace: true });
        }
      }
    }
  }, []);

  useEffect(() => {
    if (emailProviderType === 'microsoft' && microsoftEmailDetails.code) {
      setIsTeamsConnected(true);
    }
  }, [emailProviderType, microsoftEmailDetails.code]);

  return { loginWithMicrosoftTeams, handleMicrosoftTeamsRedirect, isTeamsConnected };
};
